import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * SEO优化组件
 * 提供完整的SEO元数据管理和结构化数据
 */
const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  article,
  product,
  organization,
  breadcrumbs,
  faq,
  noindex = false,
  nofollow = false,
  canonical,
  alternateLanguages,
  structuredData,
  ...props
}) => {
  const { t, currentLanguage } = useTranslation();

  // 默认SEO配置
  const defaultSEO = {
    title: t('seo.default.title', 'DecisionAI - 智能决策分析平台'),
    description: t('seo.default.description', '基于AI的智能决策分析平台，帮助企业通过数据驱动决策，提升业务效率和竞争力。'),
    keywords: t('seo.default.keywords', 'AI决策,数据分析,商业智能,企业决策,人工智能'),
    image: '/images/og-image.jpg',
    url: typeof window !== 'undefined' ? window.location.href : '',
    siteName: 'DecisionAI',
    twitterHandle: '@DecisionAI'
  };

  // 合并SEO数据
  const seoData = {
    title: title || defaultSEO.title,
    description: description || defaultSEO.description,
    keywords: keywords || defaultSEO.keywords,
    image: image || defaultSEO.image,
    url: url || defaultSEO.url,
    ...defaultSEO
  };

  // 生成完整标题
  const fullTitle = title ? `${title} | ${defaultSEO.siteName}` : defaultSEO.title;

  // 生成结构化数据
  const generateStructuredData = () => {
    const baseStructuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: defaultSEO.siteName,
      url: seoData.url,
      description: seoData.description,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${seoData.url}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string'
      }
    };

    // 组织信息
    if (organization) {
      baseStructuredData.publisher = {
        '@type': 'Organization',
        name: organization.name || defaultSEO.siteName,
        logo: {
          '@type': 'ImageObject',
          url: organization.logo || `${seoData.url}/images/logo.png`
        },
        url: organization.url || seoData.url,
        sameAs: organization.socialMedia || []
      };
    }

    // 文章结构化数据
    if (type === 'article' && article) {
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: seoData.title,
        description: seoData.description,
        image: seoData.image,
        author: {
          '@type': 'Person',
          name: article.author || 'DecisionAI Team'
        },
        publisher: baseStructuredData.publisher,
        datePublished: article.publishedTime,
        dateModified: article.modifiedTime || article.publishedTime,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': seoData.url
        }
      };
    }

    // 产品结构化数据
    if (type === 'product' && product) {
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name || seoData.title,
        description: seoData.description,
        image: seoData.image,
        brand: {
          '@type': 'Brand',
          name: defaultSEO.siteName
        },
        offers: product.offers ? {
          '@type': 'Offer',
          price: product.offers.price,
          priceCurrency: product.offers.currency || 'CNY',
          availability: product.offers.availability || 'https://schema.org/InStock'
        } : undefined
      };
    }

    // 面包屑导航
    if (breadcrumbs) {
      baseStructuredData.breadcrumb = {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url
        }))
      };
    }

    // FAQ结构化数据
    if (faq) {
      baseStructuredData.mainEntity = {
        '@type': 'FAQPage',
        mainEntity: faq.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
          }
        }))
      };
    }

    return structuredData || baseStructuredData;
  };

  // 生成robots指令
  const generateRobots = () => {
    const robots = [];
    if (noindex) robots.push('noindex');
    if (nofollow) robots.push('nofollow');
    if (robots.length === 0) robots.push('index', 'follow');
    return robots.join(', ');
  };

  return (
    <Helmet {...props}>
      {/* 基础元数据 */}
      <title>{fullTitle}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content={seoData.keywords} />
      <meta name="robots" content={generateRobots()} />
      
      {/* 语言和地区 */}
      <html lang={currentLanguage} />
      <meta name="language" content={currentLanguage} />
      
      {/* 规范链接 */}
      {canonical && <link rel="canonical" href={canonical} />}
      
      {/* 多语言支持 */}
      {alternateLanguages && alternateLanguages.map(lang => (
        <link
          key={lang.code}
          rel="alternate"
          hrefLang={lang.code}
          href={lang.url}
        />
      ))}
      
      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:image" content={seoData.image} />
      <meta property="og:url" content={seoData.url} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:locale" content={currentLanguage === 'zh' ? 'zh_CN' : 'en_US'} />
      
      {/* 文章特定的Open Graph */}
      {type === 'article' && article && (
        <>
          <meta property="article:author" content={article.author} />
          <meta property="article:published_time" content={article.publishedTime} />
          <meta property="article:modified_time" content={article.modifiedTime} />
          {article.tags && article.tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      <meta name="twitter:creator" content={defaultSEO.twitterHandle} />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={seoData.image} />
      
      {/* 移动端优化 */}
      <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* 安全性 */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-Frame-Options" content="DENY" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      
      {/* 性能优化 */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify(generateStructuredData())}
      </script>
    </Helmet>
  );
};

/**
 * 页面级SEO组件
 * 为特定页面提供SEO优化
 */
export const PageSEO = ({ page, ...props }) => {
  const { t } = useTranslation();
  
  const pageConfigs = {
    home: {
      title: t('seo.home.title', 'DecisionAI - 智能决策分析平台'),
      description: t('seo.home.description', '基于AI的智能决策分析平台，帮助企业通过数据驱动决策，提升业务效率和竞争力。'),
      keywords: t('seo.home.keywords', 'AI决策,数据分析,商业智能,企业决策,人工智能')
    },
    product: {
      title: t('seo.product.title', '产品功能 - DecisionAI'),
      description: t('seo.product.description', '了解DecisionAI的强大功能：智能数据分析、决策建议、可视化报告等。'),
      keywords: t('seo.product.keywords', '产品功能,数据分析,决策支持,可视化')
    },
    solutions: {
      title: t('seo.solutions.title', '解决方案 - DecisionAI'),
      description: t('seo.solutions.description', '针对不同行业和场景的智能决策解决方案，助力企业数字化转型。'),
      keywords: t('seo.solutions.keywords', '解决方案,行业应用,数字化转型')
    },
    cases: {
      title: t('seo.cases.title', '成功案例 - DecisionAI'),
      description: t('seo.cases.description', '查看DecisionAI在各行业的成功应用案例，了解实际效果和价值。'),
      keywords: t('seo.cases.keywords', '成功案例,客户案例,应用效果')
    },
    pricing: {
      title: t('seo.pricing.title', '价格方案 - DecisionAI'),
      description: t('seo.pricing.description', '灵活的价格方案，满足不同规模企业的需求。免费试用，按需付费。'),
      keywords: t('seo.pricing.keywords', '价格方案,免费试用,企业版')
    }
  };

  const config = pageConfigs[page] || {};
  
  return <SEO {...config} {...props} />;
};

export default SEO;
